/* @flow */

import React, { Component } from 'react';
import { connect } from 'react-redux';
import type { $BaseWidgetState } from 'CoreTypes/BaseWidgetState';
import type { $Configuration } from 'CoreTypes';
import type { $Dealer } from 'CoreTypes';
import type { $Translation } from 'CoreTypes';
import type { $StoreState } from '../../types/StoreState';
import type { $WidgetSettings } from '../../types/WidgetSettings';
import DealerActivities from 'CoreComponents/DealerActivities/DealerActivities';
import DealerAddressDistance from 'CoreComponents/DealerDistance/DealerAddressDistance';
import DealerBaseAddress from 'CoreComponents/DealerBaseAddress/DealerBaseAddress';
import DealerBaseName from 'CoreComponents/DealerBaseName/DealerBaseName';
import DealerHighlightedActivities from 'CoreComponents/DealerHighlightedActivities/DealerHighlightedActivities';
import WidgetDealerKeys from 'CoreConstants/WidgetDealerKeys';
import WidgetParagraph from 'CoreComponents/WidgetParagraph/WidgetParagraph';
import { TrackingAttributes } from 'CoreConstants/Tracking';
import WidgetDealerFields from '../../constants/WidgetDealerFields';
import WidgetFields from '../../constants/WidgetFields';
import WidgetFieldsKeys from '../../constants/WidgetFieldsKeys';
import { isFunction } from 'CoreUtils/WidgetUtils';
import { getDeepPropertyValue } from 'CoreUtils/WidgetUtils';
import { getWidgetFieldValueByKey } from 'CoreUtils/WidgetFieldUtils';
import { setSelectedDealer } from '../../store/dealer';
import { unsetSelectedDealer } from '../../store/dealer';

type $ComponentProps = {
  dealer: $Dealer,
  trackingLinkPosition: string,
  selectDealerCallback?: Function
};

type $MappedProps = {
  configuration: $Configuration,
  translations: Array<$Translation>,
  settings: $WidgetSettings,
  selectDealerButtonLabel: string,
  showSelectDealerButtonLabel: boolean,
  setSelectedDealer: Function,
  unsetSelectedDealer: Function
};

type $Props = $ComponentProps & $MappedProps;

class DealerData extends Component<$Props> {

  handleSelectDealerClick = () => {
    const { selectDealerCallback, dealer } = this.props;

    const outletId = getDeepPropertyValue(['baseInfo', 'externalId'], dealer) || '';
    this.props.setSelectedDealer(outletId, this.props.trackingLinkPosition);

    if (selectDealerCallback && isFunction(selectDealerCallback) && dealer.baseInfo) {
      selectDealerCallback({
        baseInfo: {
          outletId: dealer.baseInfo.externalId || null,
          companyId: dealer.baseInfo.companyId || null,
          name1: dealer.baseInfo.name1 || null,
          name2: dealer.baseInfo.name2 || null,
          name3: dealer.baseInfo.name3 || null,
          name4: dealer.baseInfo.name4 || null,
          name5: dealer.baseInfo.name5 || null
        },
        address: {
          country: dealer.address && dealer.address.country || null,
          city: dealer.address && dealer.address.city || null,
          region: dealer.address && dealer.address.region && dealer.address.region.region || null,
          subRegion: dealer.address && dealer.address.region && dealer.address.region.subRegion || null,
          district: dealer.address && dealer.address.district || null,
          zipcode: dealer.address && dealer.address.zipcode || null,
          line1: dealer.address && dealer.address.line1 || null,
          line2: dealer.address && dealer.address.line2 || null,
          line3: dealer.address && dealer.address.line3 || null,
          latitude: dealer.address && dealer.address.latitude || null,
          longitude: dealer.address && dealer.address.longitude || null
        }
      });
    }
  };

  render() {
    const {
      dealer,
      configuration,
      selectDealerButtonLabel,
      showSelectDealerButtonLabel,
      settings,
      translations,
      trackingLinkPosition
    } = this.props;

    const dealerDataShownOnMap = trackingLinkPosition === TrackingAttributes.LINK_POSITION_DSW_DEALER_MAP;

    const widgetState: $BaseWidgetState = {
      widgetFields: [...WidgetDealerFields, ...WidgetFields],
      dealer,
      configuration,
      translations,
      settings
    };

    return dealer ? (
      <article className="dsw-dealer-data">
        <div className="dsw-dealer-data--dealer-main-info">
          <DealerBaseName widgetState={widgetState} />
          <DealerActivities
            dealer={dealer}
            translations={this.props.translations}
            settings={this.props.settings}
            tooltipPosition='bottom'
          />
          <DealerHighlightedActivities
            configuration={this.props.configuration || {}}
            marketCode={this.props.settings.market || ''}
            dealer={this.props.dealer}
            widgetSettings={this.props.settings}
            modifierClass="h-smt-20"
          />
        </div>
        <div className="dsw-dealer-data--dealer-address-info">
          <DealerAddressDistance
            dealer={dealer}
            configuration={configuration}
            translations={translations}
            settings={settings}
            modifierClass="h-smb-20"
          />
          <DealerBaseAddress widgetState={widgetState} />
          <WidgetParagraph
            widgetFieldKey={WidgetDealerKeys.DEALER_CONTACT_PHONE}
            widgetState={widgetState}
            modifierClass={['dl-dsw-dealer-tel', 'dl-dsw-contact-data']}
          />
          <WidgetParagraph
            widgetFieldKey={WidgetDealerKeys.DEALER_CONTACT_FAX}
            widgetState={widgetState}
            modifierClass={['dl-dsw-dealer-fax', 'dl-dsw-contact-data']}
          />
          <WidgetParagraph
            widgetFieldKey={WidgetDealerKeys.DEALER_CONTACT_EMAIL}
            widgetState={widgetState}
            modifierClass={['dsw-dealer-data__email', 'dl-dsw-contact-data']}
          />
          <WidgetParagraph widgetFieldKey={WidgetDealerKeys.DEALER_HOMEPAGE} widgetState={widgetState} />
          <button
            type="button"
            aria-label="Select a dealer"
            id="dsw-search-list-item-select"
            onClick={this.handleSelectDealerClick}
            className={`dsw-search-result-list__select wb-button ` +
              `${dealerDataShownOnMap ? 'wb-button--primary' : 'wb-button--secondary'} wb-button--small`}
            >
            <wb-icon
              name="chevron-right"
              aria-hidden="true"
              class="dsw-search-result-list__select--ico wb-button__icon">
            </wb-icon>
            {(showSelectDealerButtonLabel && selectDealerButtonLabel) || ''}
          </button>
          {/*<button
            type="button"
            className="dsw-search-result-list__select wb-e-btn wb-e-btn-3"
            id="dsw-search-list-item-select"
            onClick={this.handleSelectDealerClick}
            style={{ marginTop: '20px' }}>
            <WorkbenchIcon icon={WorkbenchIcons.ICO_ARROW_4} modifierClass="dsw-search-result-list__select--ico" />
            <span>{(showSelectDealerButtonLabel && selectDealerButtonLabel) || ''}</span>
          </button>*/}
        </div>
      </article>
    ) : null;
  }
}

const mapStateToProps = (state: $StoreState) => ({
  configuration: state.dsw.configuration,
  translations: state.dsw.translations,
  settings: state.dsw.settings,
  selectDealerButtonLabel: getWidgetFieldValueByKey(WidgetFieldsKeys.BUTTON_DEALER_SELECT, state.dsw),
  showSelectDealerButtonLabel: getWidgetFieldValueByKey(WidgetFieldsKeys.BUTTON_DEALER_SELECT, state.dsw)
});

const mapDispatchToProps: Object = {
  setSelectedDealer,
  unsetSelectedDealer
};

export default connect(mapStateToProps, mapDispatchToProps)(DealerData);
